/**
 * @nanokart/tryon-studio-sdk
 * SizeRecommendPanel — AI Size Advisor (Premium Redesign)
 *
 * White + deep-blue brand theme. Shown below the try-on result when:
 *   1. A successful try-on exists for the current product
 *   2. The product has a size chart (or one can be fetched)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { usePhoto } from '../../core/hooks/usePhoto';
import { useTryOnStore } from '../../core/hooks/useStore';
import type { SizeRecommendResult } from '../../core/state/types';

// ── Global CSS (injected once) ──────────────────────────────────────────────
const PANEL_CSS = `
  @keyframes nkSzSlideDown {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes nkSzFadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes nkSzPop {
    0%   { transform: scale(0.4); opacity: 0; }
    65%  { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes nkSzGlow {
    0%, 100% { box-shadow: 0 0 20px 4px rgba(99,179,237,0.35), 0 0 0 8px rgba(59,130,246,0.08); }
    50%      { box-shadow: 0 0 32px 8px rgba(99,179,237,0.55), 0 0 0 12px rgba(59,130,246,0.12); }
  }
  @keyframes nkSzArc {
    from { stroke-dashoffset: 219.9; }
  }
  @keyframes nkSzMeterFill {
    from { stroke-dashoffset: 165; }
  }
  @keyframes nkSzRingFill {
    from { stroke-dashoffset: 277; }
  }
  @keyframes nkSzPulse {
    0%, 80%, 100% { opacity: 0.25; transform: scale(0.75); }
    40%           { opacity: 1;    transform: scale(1); }
  }
  @keyframes nkSzShimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes nkSzRingPulse {
    0%, 100% { opacity: 0.5; transform: scale(1); }
    50%      { opacity: 0.9; transform: scale(1.04); }
  }
  .nk-sz-btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(37,99,235,0.45) !important;
  }
  .nk-sz-btn-primary:active:not(:disabled) {
    transform: translateY(0);
  }
  .nk-sz-toggle:hover {
    background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%) !important;
  }
  .nk-sz-refine:hover {
    color: #2563eb !important;
    text-decoration: underline;
  }
`;

// ── Icons ────────────────────────────────────────────────────────────────────

const SparkIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

// ── Pulsing loader dots ───────────────────────────────────────────────────────
const PulsingDots = () => (
  <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
    {[0, 1, 2].map(i => (
      <span key={i} style={{
        width: 5, height: 5, borderRadius: '50%', background: 'currentColor',
        animation: `nkSzPulse 1.2s ${i * 0.2}s ease-in-out infinite`,
      }} />
    ))}
  </span>
);

// ── Height helpers ────────────────────────────────────────────────────────────
function ftInToCm(ftStr: string): number | null {
  const clean = ftStr.replace(/['"ft\s]+/g, ' ').trim();
  const parts = clean.split(/\s+/);
  const ft = parseFloat(parts[0]);
  const inches = parts.length > 1 ? parseFloat(parts[1]) : 0;
  if (isNaN(ft)) return null;
  return Math.round((ft * 12 + (isNaN(inches) ? 0 : inches)) * 2.54);
}

function parseHeightCm(value: string, unit: 'cm' | 'ft'): number | null {
  if (!value.trim()) return null;
  if (unit === 'cm') {
    const v = parseFloat(value);
    return isNaN(v) ? null : Math.round(v);
  }
  return ftInToCm(value);
}

// ── Colored progress ring around size badge ────────────────────────────────
const RING_R = 44;
const RING_C = 2 * Math.PI * RING_R; // ≈ 276.5

function getZoneColor(pct: number): string {
  if (pct < 45) return '#f97316'; // orange-red  (low)
  if (pct < 70) return '#eab308'; // amber-yellow (medium)
  return '#22c55e';               // green        (strong)
}

const SizeBadgeWithRing: React.FC<{ size: string; percent: number | null }> = ({ size, percent }) => {
  const zoneColor  = percent != null ? getZoneColor(percent) : 'rgba(255,255,255,0.3)';
  const filled     = percent != null ? (percent / 100) * RING_C : 0;
  const dashOffset = RING_C - filled;

  return (
    <div style={{ position: 'relative', width: 100, height: 100, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* Colored SVG ring */}
      <svg width="100" height="100" viewBox="0 0 100 100"
        style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
      >
        <defs>
          <filter id="nkRingGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {/* Track */}
        <circle cx="50" cy="50" r={RING_R} fill="none"
          stroke="rgba(255,255,255,0.1)" strokeWidth="5" />
        {/* Colored progress */}
        {percent != null && (
          <circle cx="50" cy="50" r={RING_R} fill="none"
            stroke={zoneColor}
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={`${RING_C} 9999`}
            strokeDashoffset={dashOffset}
            style={{
              animation: 'nkSzRingFill 1.1s cubic-bezier(0.22,1,0.36,1) forwards',
              filter: `drop-shadow(0 0 5px ${zoneColor}90)`,
            }}
          />
        )}
      </svg>

      {/* Size badge */}
      <div
        data-testid="recommended-size-badge"
        style={{
          width: 76, height: 76, borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(12px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'nkSzPop 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.2s both',
        }}
      >
        <span style={{
          fontSize: size.length > 3 ? 20 : size.length > 2 ? 24 : 30,
          fontWeight: 900, color: '#fff',
          letterSpacing: '-0.03em', lineHeight: 1,
        }}>
          {size}
        </span>
      </div>
    </div>
  );
};

// ── Result Card ───────────────────────────────────────────────────────────────
const SizeResultCard: React.FC<{ result: SizeRecommendResult; onRefine: () => void }> = ({ result, onRefine }) => {
  // Build combined notes: merge fitNotes and insufficiencyMessage when photo is insufficient
  const combinedNotes = React.useMemo(() => {
    if (result.photoSufficient) {
      return result.fitNotes || null;
    }
    // Photo insufficient - merge both messages for context
    const parts: string[] = [];
    if (result.insufficiencyMessage) parts.push(result.insufficiencyMessage);
    if (result.fitNotes) parts.push(result.fitNotes);
    return parts.length > 0 ? parts.join(' ') : null;
  }, [result.photoSufficient, result.fitNotes, result.insufficiencyMessage]);

  // Determine display confidence: show "<10%" when photo_sufficient is false and confidence is 0
  const displayConfidence = React.useMemo(() => {
    const conf = result.confidencePercent ?? 0;
    if (!result.photoSufficient && conf === 0) {
      return '<10';
    }
    return conf.toString();
  }, [result.photoSufficient, result.confidencePercent]);

  // Get effective confidence for color calculation (use 5 for "<10%" display to get red)
  const effectiveConfidenceForColor = React.useMemo(() => {
    const conf = result.confidencePercent ?? 0;
    if (!result.photoSufficient && conf === 0) {
      return 5; // This will show red color
    }
    return conf;
  }, [result.photoSufficient, result.confidencePercent]);

  return (
    <div
      data-testid="size-recommendation-result"
      style={{
        marginTop: 12,
        borderRadius: 16,
        overflow: 'hidden',
        animation: 'nkSzFadeUp 0.4s cubic-bezier(0.22,1,0.36,1)',
      }}
    >
      {/* ── Dark gradient hero card — always blue background for consistency ── */}
      <div style={{
        background: 'linear-gradient(145deg, #0d1b3e 0%, #1a3070 45%, #1e40af 100%)',
        padding: '24px 20px 20px',
        position: 'relative',
        overflow: 'hidden',
        textAlign: 'center',
      }}>
        {/* Decorative mesh */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at 80% 20%, rgba(59,130,246,0.25) 0%, transparent 60%), radial-gradient(ellipse at 10% 80%, rgba(147,197,253,0.12) 0%, transparent 50%)',
        }} />
        {/* Subtle dot grid */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.07,
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }} />

        {/* Photo quality warning banner (only when insufficient) */}
        {!result.photoSufficient && (
          <div style={{
            position: 'relative',
            marginBottom: 14,
            padding: '8px 12px',
            background: 'rgba(239,68,68,0.15)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 10,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            animation: 'nkSzFadeUp 0.4s 0.1s both',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span style={{ fontSize: 10, fontWeight: 600, color: '#fca5a5', letterSpacing: '0.02em' }}>
              Low confidence due to photo quality
            </span>
          </div>
        )}

        {/* 1. "Recommended Size" caption */}
        <div style={{
          fontSize: 9, fontWeight: 700,
          color: 'rgba(147,197,253,0.6)',
          letterSpacing: '0.14em', textTransform: 'uppercase',
          marginBottom: 10, position: 'relative',
          animation: 'nkSzFadeUp 0.4s 0.2s both',
        }}>
          Recommended Size
        </div>

        {/* 2. Size badge with colored progress ring */}
        <div style={{ position: 'relative' }}>
          <SizeBadgeWithRing
            size={result.recommendedSize}
            percent={effectiveConfidenceForColor}
          />
        </div>

        {/* 3. Confidence percentage */}
        {result.confidencePercent != null && (
          <div style={{ marginTop: 12, position: 'relative' }}>
            <div style={{
              fontSize: 30, fontWeight: 900, lineHeight: 1,
              color: getZoneColor(effectiveConfidenceForColor),
              letterSpacing: '-0.03em',
              textShadow: `0 0 20px ${getZoneColor(effectiveConfidenceForColor)}60`,
              animation: 'nkSzFadeUp 0.4s 0.6s both',
            }}>
              {displayConfidence}%
            </div>
            <div style={{
              marginTop: 4, fontSize: 9.5, fontWeight: 700,
              color: 'rgba(255,255,255,0.45)', letterSpacing: '0.12em',
              textTransform: 'uppercase',
              animation: 'nkSzFadeUp 0.4s 0.75s both',
            }}>
              match confidence
            </div>
          </div>
        )}
      </div>

      {/* Combined notes (fit notes + insufficiency message when applicable) */}
      {combinedNotes && (
        <div style={{
          padding: '14px 16px',
          background: 'linear-gradient(to bottom, #f0f7ff, #f8fbff)',
          borderTop: '1px solid rgba(37,99,235,0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
            <div style={{
              width: 22, height: 22, borderRadius: 7,
              background: result.photoSufficient
                ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
                : 'linear-gradient(135deg, #ef4444, #dc2626)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, marginTop: 1,
            }}>
              {result.photoSufficient ? (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              )}
            </div>
            <p style={{
              margin: 0, fontSize: 12,
              color: result.photoSufficient ? '#374151' : '#7f1d1d',
              lineHeight: 1.65,
            }}>
              {combinedNotes}
            </p>
          </div>
        </div>
      )}

    {/* ── Refine / clear (always shown) ── */}
    <div style={{
      padding: '10px 16px',
      background: '#f8fbff',
      borderTop: '1px solid rgba(37,99,235,0.06)',
      display: 'flex', justifyContent: 'center',
    }}>
      <button
        className="nk-sz-refine"
        onClick={onRefine}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 11, color: '#94a3b8', fontWeight: 500,
          transition: 'color 0.15s',
          display: 'flex', alignItems: 'center', gap: 4,
        }}
        data-testid="size-recommend-refine-btn"
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 .49-5.18"/>
        </svg>
        Try a different height
      </button>
    </div>
  </div>
  );
};
// ── Input Section ─────────────────────────────────────────────────────────────
interface InputSectionProps {
  unit: 'cm' | 'ft';
  heightInput: string;
  feetInput: string;
  inchesInput: string;
  isLoading: boolean;
  isValid: boolean;
  heightCm: number | null;
  error: string | null;
  onUnitChange: (u: 'cm' | 'ft') => void;
  onHeightChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFeetChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInchesChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
}

const InputSection: React.FC<InputSectionProps> = ({
  unit, heightInput, feetInput, inchesInput, isLoading, isValid, heightCm, error,
  onUnitChange, onHeightChange, onFeetChange, onInchesChange, onSubmit,
}) => {
  return (
    <div style={{ animation: 'nkSzSlideDown 0.3s cubic-bezier(0.22,1,0.36,1)' }}>
      {/* Heading */}
      <div style={{ marginBottom: 14, textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
          Enter your height to find your size
        </p>
        <p style={{ margin: '3px 0 0', fontSize: 11, color: '#94a3b8' }}>
          AI uses your body measurements from the try-on
        </p>
      </div>

      {/* Unit toggle */}
      <div style={{
        display: 'flex',
        background: '#f1f5f9', borderRadius: 12, padding: 3,
        marginBottom: 12, gap: 2,
      }}>
        {(['cm', 'ft'] as const).map(u => (
          <button
            key={u}
            data-testid={`size-unit-${u}`}
            onClick={() => onUnitChange(u)}
            style={{
              flex: 1, padding: '7px 10px', borderRadius: 9,
              border: 'none', cursor: 'pointer',
              background: unit === u
                ? 'linear-gradient(135deg, #2563eb, #1d4ed8)'
                : 'transparent',
              color: unit === u ? '#fff' : '#64748b',
              fontSize: 12, fontWeight: unit === u ? 700 : 500,
              boxShadow: unit === u ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
              transition: 'all 0.2s',
            }}
          >
            {u === 'ft' ? 'ft / in' : 'cm'}
          </button>
        ))}
      </div>

      {/* Input + button row */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        {unit === 'cm' ? (
          /* ── Single CM input ── */
          <div style={{
            flex: 1,
            background: '#f8faff',
            border: '1.5px solid',
            borderColor: heightInput && !isValid ? '#fca5a5' : '#dbeafe',
            borderRadius: 13,
            display: 'flex', alignItems: 'center',
            overflow: 'hidden',
          }}>
            <input
              data-testid="size-height-input"
              type="text"
              inputMode="decimal"
              value={heightInput}
              onChange={onHeightChange}
              placeholder="e.g. 165"
              style={{
                flex: 1, padding: '11px 12px',
                border: 'none', background: 'transparent',
                fontSize: 17, fontWeight: 700, color: '#1e293b',
                outline: 'none', letterSpacing: '0.01em',
              }}
            />
            <span style={{
              paddingRight: 13, fontSize: 10, fontWeight: 800,
              color: '#3b82f6', letterSpacing: '0.08em', textTransform: 'uppercase',
            }}>cm</span>
          </div>
        ) : (
          /* ── Split FT + IN inputs ── */
          <div style={{ flex: 1, display: 'flex', gap: 6 }}>
            <div style={{
              flex: 1,
              background: '#f8faff',
              border: '1.5px solid #dbeafe',
              borderRadius: 13,
              display: 'flex', alignItems: 'center',
              overflow: 'hidden',
            }}>
              <input
                data-testid="size-height-input"
                type="text"
                inputMode="numeric"
                value={feetInput}
                onChange={onFeetChange}
                placeholder="5"
                style={{
                  flex: 1, padding: '11px 10px',
                  border: 'none', background: 'transparent',
                  fontSize: 17, fontWeight: 700, color: '#1e293b',
                  outline: 'none', textAlign: 'center',
                }}
              />
              <span style={{
                paddingRight: 10, fontSize: 10, fontWeight: 800,
                color: '#3b82f6', letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>ft</span>
            </div>
            <div style={{
              flex: 1,
              background: '#f8faff',
              border: '1.5px solid #dbeafe',
              borderRadius: 13,
              display: 'flex', alignItems: 'center',
              overflow: 'hidden',
            }}>
              <input
                data-testid="size-inches-input"
                type="text"
                inputMode="numeric"
                value={inchesInput}
                onChange={onInchesChange}
                placeholder="7"
                style={{
                  flex: 1, padding: '11px 10px',
                  border: 'none', background: 'transparent',
                  fontSize: 17, fontWeight: 700, color: '#1e293b',
                  outline: 'none', textAlign: 'center',
                }}
              />
              <span style={{
                paddingRight: 10, fontSize: 10, fontWeight: 800,
                color: '#3b82f6', letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>in</span>
            </div>
          </div>
        )}

        {/* Submit */}
        <button
          className="nk-sz-btn-primary"
          data-testid="size-recommend-submit"
          onClick={onSubmit}
          disabled={!isValid || isLoading}
          style={{
            padding: '0 16px',
            minHeight: 46, minWidth: 46,
            background: isValid && !isLoading
              ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
              : '#e2e8f0',
            color: isValid && !isLoading ? '#fff' : '#94a3b8',
            border: 'none', borderRadius: 13,
            fontSize: 13, fontWeight: 700,
            cursor: isValid && !isLoading ? 'pointer' : 'default',
            boxShadow: isValid && !isLoading ? '0 4px 14px rgba(37,99,235,0.35)' : 'none',
            transition: 'all 0.2s cubic-bezier(0.22,1,0.36,1)',
            whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', gap: 7,
          }}
        >
          {isLoading ? (
            <><PulsingDots /><span>Analyzing</span></>
          ) : (
            <><span>Get Size</span><ArrowRight /></>
          )}
        </button>
      </div>

      {/* ft cm hint */}
      {unit === 'ft' && (feetInput || inchesInput) && heightCm && (
        <div style={{ marginTop: 5, fontSize: 11, color: '#94a3b8' }}>
          ≈ {heightCm} cm
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          data-testid="size-recommend-error"
          style={{
            marginTop: 10, padding: '10px 13px',
            background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: 11, fontSize: 12, color: '#dc2626',
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </div>
      )}
    </div>
  );
};

// ── Main Panel ────────────────────────────────────────────────────────────────
interface SizeRecommendPanelProps {
  productId: string;
  style?: React.CSSProperties;
}

export const SizeRecommendPanel: React.FC<SizeRecommendPanelProps> = ({ productId, style }) => {
  const { hasPhoto } = usePhoto();
  const store = useTryOnStore();
  const { sizeRecommend, fetchSizeRecommendation, setSizeHeightInput, checkSizeAvailability, clearSizeRecommendation } = store;

  const [expanded, setExpanded] = useState(false);
  const [heightInput, setHeightInputLocal] = useState(sizeRecommend.heightInput);
  const [feetInput, setFeetInput] = useState('');
  const [inchesInput, setInchesInput] = useState('');
  const [unit, setUnit] = useState<'cm' | 'ft'>(sizeRecommend.heightUnit);

  const cachedResult = sizeRecommend.results[productId];
  const isAvailable = sizeRecommend.isAvailable;

  // Derived: combined ft string for parsing
  const ftCombined = `${feetInput} ${inchesInput}`.trim();
  const effectiveInput = unit === 'ft' ? ftCombined : heightInput;

  // Hooks before any conditional return
  const handleGetSize = useCallback(async () => {
    const heightCm = parseHeightCm(effectiveInput, unit);
    if (!heightCm || heightCm < 100 || heightCm > 250) return;
    await fetchSizeRecommendation(productId, heightCm);
  }, [effectiveInput, unit, productId, fetchSizeRecommendation]);

  useEffect(() => {
    checkSizeAvailability(productId);
  }, [productId, checkSizeAvailability]);

  useEffect(() => {
    clearSizeRecommendation(productId);
    setExpanded(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // Auto-expand when a result arrives
  useEffect(() => {
    if (cachedResult) setExpanded(true);
  }, [cachedResult]);

  if (!hasPhoto || !isAvailable) return null;

  const handleUnitChange = (newUnit: 'cm' | 'ft') => {
    setUnit(newUnit);
    setHeightInputLocal('');
    setFeetInput('');
    setInchesInput('');
    setSizeHeightInput('', newUnit);
  };

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHeightInputLocal(e.target.value);
    setSizeHeightInput(e.target.value, unit);
  };

  const handleFeetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setFeetInput(val);
    setSizeHeightInput(`${val} ${inchesInput}`.trim(), 'ft');
  };

  const handleInchesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInchesInput(val);
    setSizeHeightInput(`${feetInput} ${val}`.trim(), 'ft');
  };

  const heightCm = parseHeightCm(effectiveInput, unit);
  const isValidHeight = !!heightCm && heightCm >= 100 && heightCm <= 250;

  const handleRefine = () => {
    clearSizeRecommendation(productId);
    setHeightInputLocal('');
    setFeetInput('');
    setInchesInput('');
    setSizeHeightInput('', unit);
  };

  return (
    <>
      <style>{PANEL_CSS}</style>
      <div
        data-testid="size-recommend-panel"
        style={{
          marginTop: 12,
          borderRadius: 20,
          overflow: 'hidden',
          background: '#fff',
          border: '1px solid rgba(37,99,235,0.15)',
          boxShadow: '0 2px 20px rgba(37,99,235,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
          ...style,
        }}
      >
        {/* ── Toggle header ── */}
        <button
          className="nk-sz-toggle"
          data-testid="size-recommend-toggle"
          onClick={() => setExpanded(v => !v)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between',
            padding: '13px 15px',
            background: expanded
              ? 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)'
              : '#fff',
            border: 'none',
            borderBottom: expanded ? '1px solid rgba(37,99,235,0.1)' : 'none',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            {/* Icon tile */}
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 60%, #3b82f6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff',
              boxShadow: '0 3px 10px rgba(37,99,235,0.4)',
              flexShrink: 0,
            }}>
              <SparkIcon />
            </div>

            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>
                Find Your Perfect Size
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
                {cachedResult
                  ? <>Recommended: <strong style={{ color: cachedResult.photoSufficient ? '#2563eb' : '#ef4444' }}>{cachedResult.recommendedSize}</strong>{!cachedResult.photoSufficient && <span style={{ color: '#ef4444' }}> (low confidence)</span>}</>
                  : 'AI-powered fit recommendation'}

              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Size pill — always show when result exists */}
            {cachedResult && !expanded && (
              <div style={{
                background: cachedResult.photoSufficient
                  ? 'linear-gradient(135deg, #1d4ed8, #2563eb)'
                  : 'linear-gradient(135deg, #dc2626, #ef4444)',
                color: '#fff', fontSize: 12, fontWeight: 800,
                padding: '3px 11px', borderRadius: 99,
                boxShadow: cachedResult.photoSufficient
                  ? '0 2px 8px rgba(37,99,235,0.35)'
                  : '0 2px 8px rgba(239,68,68,0.35)',
                letterSpacing: '0.02em',
              }}>
                {cachedResult.recommendedSize}
              </div>
            )}
            {/* Chevron tile */}
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: expanded ? '#dbeafe' : '#eff6ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#2563eb',
              transition: 'background 0.2s',
            }}>
              <ChevronIcon open={expanded} />
            </div>
          </div>
        </button>

        {/* ── Body ── */}
        {expanded && (
          <div style={{ padding: '16px 15px 15px' }}>
            {cachedResult ? (
              <SizeResultCard result={cachedResult} onRefine={handleRefine} />
            ) : (
              <InputSection
                unit={unit}
                heightInput={heightInput}
                feetInput={feetInput}
                inchesInput={inchesInput}
                isLoading={sizeRecommend.isLoading}
                isValid={isValidHeight}
                heightCm={heightCm}
                error={sizeRecommend.error}
                onUnitChange={handleUnitChange}
                onHeightChange={handleHeightChange}
                onFeetChange={handleFeetChange}
                onInchesChange={handleInchesChange}
                onSubmit={handleGetSize}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};
